import {
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import useResponsive from "../../hooks/useResponsive";
import { ProjectMembersDto } from "../../types/projectTypes";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { MembersFieldDto } from "../../types/membersTypes";
import useMembers from "../../hooks/members/useMembers";
import useDepartments from "../../hooks/members/useDepartments";
import PreviousProjectAppications from "../../components/projects/projectDetails/PreviousProjectAppications";
import { Groups } from "@mui/icons-material";
import useProjectsDetails from "../../hooks/projects/useProjectsDetails";
import LoadingCircle from "../../components/general/LoadingCircle";
import EditProjectStaffingCommitteeDialog from "../../components/projects/EditProjectStaffingCommitteeDialog";

/**
 * Component to display the project team composition page
 * @returns - A form to enter the project team composition data
 */
const ProjectTeamComposition = () => {
  const theme = useTheme();
  const styles = {
    tableHeaderCell: {
      color: "white",
      fontWeight: "bold",
    },
    tableHeader: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      textAlign: "center",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    tableRow: {
      "&:nth-of-type(odd)": { backgroundColor: "#fff" },
      "&:nth-of-type(even)": { backgroundColor: "#ededed" },
      textDecoration: "none",
    },
    tableCell: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      paddingRight: 0,
    },
  };

  const { id } = useParams<{ id: string }>();
  const { members } = useMembers();
  const { currentDirectors } = useDepartments();
  const isMobile = useResponsive("down", "sm");
  const { projectDetails, isProjectDetailsLoading, saveStaffingCommittee, saveTeamComposition } = useProjectsDetails(
    Number(id)
  );

  const [projectMembers, setProjectMembers] = React.useState<ProjectMembersDto[]>([]);
  const [projectManager, setProjectManager] = React.useState<ProjectMembersDto | null>(null);

  // Map current directors to MembersFieldDto
  const staffingDirectors = currentDirectors
    .filter(
      (director) =>
        director.directorId === 1 ||
        director.directorId === 2 ||
        director.directorId === 3 ||
        director.directorId === 4 ||
        director.directorId === 5
    )
    .map(
      (director) =>
        ({
          memberId: director.memberId,
          firstname: director.firstname,
          lastname: director.lastname,
        } as MembersFieldDto)
    );
  // Map members to MembersFieldDto
  const selectableStaffingCommittee = [
    ...members
      .filter((member) => member.memberStatus?.name === "Senior")
      .map(
        (member) =>
          ({
            memberId: member.memberId,
            firstname: member.firstname,
            lastname: member.lastname,
          } as MembersFieldDto)
      ),
    ...staffingDirectors,
  ];

  const [staffingCommittee, setStaffingCommittee] = React.useState<MembersFieldDto[]>(
    projectDetails?.staffingCommittee || []
  );

  useEffect(() => {
    setStaffingCommittee(projectDetails?.staffingCommittee || []);
  }, [projectDetails]);

  const [notes, setNotes] = React.useState<string>(projectDetails?.notes || "");
  const [kickOffDate, setKickOffDate] = React.useState<Dayjs | undefined>(dayjs(projectDetails?.kickoff) || undefined);

  const [openPreviousApplications, setOpenPreviousApplications] = React.useState(false);
  const [selectedMember, setSelectedMember] = React.useState<ProjectMembersDto | null>(null);

  const [openStaffingCommittee, setOpenStaffingCommittee] = React.useState(false);

  const handleOpenStaffingCommitteeDialog = () => {
    setOpenStaffingCommittee(true);
  };

  const handleCloseStaffingCommitteeDialog = () => {
    setStaffingCommittee(projectDetails?.staffingCommittee || []);
    setOpenStaffingCommittee(false);
  };

  const handleSaveStaffingCommittee = () => {
    if (!staffingCommittee) {
      return;
    }
    const updatedStaffingCommittee = staffingCommittee.filter((member) => member.memberId !== undefined);
    setStaffingCommittee(updatedStaffingCommittee);
    saveStaffingCommittee(updatedStaffingCommittee);
    handleCloseStaffingCommitteeDialog();
  };

  // Check if any project member is in the staffing committee
  const projectMemberInStaffingCommittee = staffingCommittee.some((committeeMember) =>
    projectMembers.some((projectMember) => projectMember.memberId === committeeMember.memberId)
  );

  // Handle notes change
  const onChangeNotes = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(event.target.value);
  };

  // Function to handle the change of the kickoff date
  const onChangeEstimatedProjectStart = (date: Dayjs | null) => {
    date ? setKickOffDate(date) : setKickOffDate(undefined);
  };

  // Function to handle the selection of a member
  const handleStaffingCommitteeSelection = (event: React.SyntheticEvent, value: MembersFieldDto | null) => {
    const index = Number(event.currentTarget.id.split("-")[1]);
    if (value !== null) {
      const updatedMembers = [...staffingCommittee];
      updatedMembers[index] = value;
      setStaffingCommittee(updatedMembers);
    }
  };

  // Function to add a member to the list of members
  const addStaffingCommitteeMember = () => {
    setStaffingCommittee((prev) => [...prev, [] as unknown as MembersFieldDto]);
  };

  // Function to remove a member from the list of members
  const removeStaffingCommitteeMember = (memberId: number) => {
    const updatedMembers = staffingCommittee.filter((member) => member.memberId !== memberId);
    setStaffingCommittee(updatedMembers);
  };

  // Open the dialog for the previous applications
  const handleOpenPreviousApplications = (memberId: number) => {
    setSelectedMember(projectDetails?.members.find((applicant) => applicant.memberId === memberId) || null);
    setOpenPreviousApplications(true);
  };

  // Close the dialog for the previous applications
  const handleClosePreviousApplications = () => {
    setOpenPreviousApplications(false);
  };

  // Function to handle the selection of the project manager
  const onChangeProjectManager = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newProjectManager = projectDetails?.members.find((member) => member.memberId === Number(event.target.value));
    if (newProjectManager) {
      setProjectManager(newProjectManager);
      if (!projectMembers.some((member) => member.memberId === newProjectManager.memberId)) {
        setProjectMembers([...projectMembers, newProjectManager]);
      }
    }
  };

  // Function to handle the selection of the project members
  const onChangeProjectMembers = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMember = projectDetails?.members.find((member) => member.memberId === Number(event.target.value));
    if (newMember) {
      if (projectMembers.some((member) => member.memberId === newMember.memberId)) {
        setProjectMembers(projectMembers.filter((member) => member.memberId !== newMember.memberId));
      } else {
        setProjectMembers([...projectMembers, newMember]);
      }
    }
  };

  // Function to handle the saving of the team composition
  const handleSaveTeamComposition = async () => {
    if (!kickOffDate || !staffingCommittee || !projectManager || !projectMembers) {
      return;
    }
    try {
      await saveTeamComposition({
        projectManager: projectManager,
        projectMembers: projectMembers,
        notes: notes,
        kickOffDate: kickOffDate.toDate(),
      });
    } catch (error) {
      // error is handled by the useProjectsDetails hook
    }
  };

  if (isProjectDetailsLoading) {
    return <LoadingCircle />;
  }

  if (!projectDetails || !members || !currentDirectors) {
    return null;
  }

  return (
    <Container>
      <EditProjectStaffingCommitteeDialog
        open={openStaffingCommittee}
        onClose={handleCloseStaffingCommitteeDialog}
        handleStaffingCommitteeSelection={handleStaffingCommitteeSelection}
        addStaffingCommitteeMember={addStaffingCommitteeMember}
        removeStaffingCommitteeMember={removeStaffingCommitteeMember}
        staffingCommittee={staffingCommittee}
        selectableStaffingCommittee={selectableStaffingCommittee}
        handleSaveStaffingCommittee={handleSaveStaffingCommittee}
      />
      {openPreviousApplications && selectedMember ? (
        <Dialog onClose={handleClosePreviousApplications} open={openPreviousApplications} maxWidth="md" fullWidth>
          <DialogTitle borderBottom={1} borderColor={"primary.main"}>
            Alte Projektbewerbungen von {selectedMember.firstname} {selectedMember.lastname}
          </DialogTitle>
          <DialogContent>
            <PreviousProjectAppications
              memberId={selectedMember.memberId}
              previousApplications={selectedMember.previousApplications || []}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePreviousApplications} color="primary">
              Schließen
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{ marginBottom: 2 }}
      >
        <Typography variant="h5" component="h1" fontWeight={"bold"}>
          Zusammenstellung Projektteam
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          fullWidth={!!isMobile}
          onClick={handleOpenStaffingCommitteeDialog}
        >
          <Groups />
          <Typography sx={{ marginLeft: 1 }}>Besetzungsgremium bearbeiten</Typography>
        </Button>
      </Stack>
      <Paper sx={{ padding: 2 }}>
        <Stack spacing={1}>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
            alignItems={isMobile ? "start" : "center"}
          >
            <Typography fontWeight={"bold"} sx={{ flex: 1 }}>
              Projektname:
            </Typography>
            <Typography sx={{ flex: 3 }}>{projectDetails?.projectName}</Typography>
          </Stack>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
            alignItems={isMobile ? "start" : "center"}
          >
            <Typography fontWeight={"bold"} sx={{ flex: 1 }}>
              Unternehmen:
            </Typography>
            <Typography sx={{ flex: 3 }}>{projectDetails?.client.name}</Typography>
          </Stack>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
            alignItems={isMobile ? "start" : "center"}
          >
            <Typography fontWeight={"bold"} sx={{ flex: 1 }}>
              Branche:
            </Typography>
            <Typography sx={{ flex: 3 }}>{projectDetails?.client.industry.description}</Typography>
          </Stack>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
            alignItems={isMobile ? "start" : "center"}
          >
            <Typography fontWeight={"bold"} sx={{ flex: 1 }}>
              Kernkompetenzen:
            </Typography>
            <Typography sx={{ flex: 3 }}>
              {projectDetails.coreCompetencies.map((c) => c.designation).join(", ")}
            </Typography>
          </Stack>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
            alignItems={isMobile ? "start" : "center"}
          >
            <Typography fontWeight={"bold"} sx={{ flex: 1 }}>
              Projektmitglieder (Min):
            </Typography>
            <Typography sx={{ flex: 3 }}>{projectDetails?.estimatedProjectMemberMin}</Typography>
          </Stack>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
            alignItems={isMobile ? "start" : "center"}
          >
            <Typography fontWeight={"bold"} sx={{ flex: 1 }}>
              Projektmitglieder (Max):
            </Typography>
            <Typography sx={{ flex: 3 }}>{projectDetails?.estimatedProjectMemberMax}</Typography>
          </Stack>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
            alignItems={isMobile ? "start" : "center"}
          >
            <Typography fontWeight={"bold"} sx={{ flex: 1 }}>
              Bemerkungen:
            </Typography>
            <TextField
              sx={{ flex: 3, width: "100%" }}
              variant="outlined"
              size="small"
              multiline
              minRows={3}
              value={notes}
              onChange={onChangeNotes}
            />
          </Stack>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
            alignItems={isMobile ? "start" : "center"}
          >
            <Typography fontWeight={"bold"} sx={{ flex: 1 }}>
              Kick-Off:
            </Typography>
            <Stack direction={"column"} sx={{ flex: 3, width: "100%" }}>
              <DatePicker
                slotProps={{
                  textField: {
                    variant: "outlined",
                    size: "small",
                  },
                }}
                minDate={dayjs()}
                value={kickOffDate}
                onChange={onChangeEstimatedProjectStart}
              />
              {!kickOffDate || kickOffDate < dayjs() ? (
                <Typography variant={"caption"} color={"error"}>
                  Kickof Datum muss in der Zukunft liegen.
                </Typography>
              ) : null}
            </Stack>
          </Stack>
          <Stack direction={isMobile ? "column" : "row"} justifyContent={"space-between"} alignItems={"start"}>
            <Typography fontWeight={"bold"} sx={{ flex: 1 }}>
              Besetzungsgremium:
            </Typography>
            <Box sx={{ flex: 3 }}>
              <Typography>
                {projectDetails?.staffingCommittee.map((member, index) => (
                  <React.Fragment key={member.memberId}>
                    <Link
                      to={`/gesamtuebersicht/${member.memberId}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      {member.firstname} {member.lastname}
                    </Link>
                    {index < projectDetails?.staffingCommittee.length - 2 && ", "}
                    {index === projectDetails?.staffingCommittee.length - 2 && " und "}
                  </React.Fragment>
                ))}
              </Typography>
              {projectDetails?.staffingCommittee.length < 4 || projectDetails?.staffingCommittee.length > 6 ? (
                <Typography variant={"caption"} color={"error"}>
                  Es müssen zwischen 4 und 6 Mitglieder im Besetzungsgremium sein.
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </Stack>
      </Paper>
      <Divider sx={{ borderColor: "primary.main", mt: 2, mb: 2 }} />
      <Typography variant="h6" fontWeight={"bold"}>
        Team zusammenstellen
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={styles.tableHeader}>
            <TableRow>
              <TableCell sx={styles.tableHeaderCell}>PM?</TableCell>
              <TableCell sx={styles.tableHeaderCell}>PL?</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Name, Vorname</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Mitgliedstatus</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Datum</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Bewerbung</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Vorherige Bewerbungen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projectDetails.members.map((applicant) => (
              <TableRow key={applicant.memberId} sx={styles.tableRow}>
                <TableCell>
                  <Checkbox
                    checked={
                      projectMembers.some((member) => member.memberId === applicant.memberId) ||
                      projectManager?.memberId === applicant.memberId
                    }
                    size="small"
                    onChange={onChangeProjectMembers}
                    value={applicant.memberId}
                    disabled={projectManager?.memberId === applicant.memberId}
                  />
                </TableCell>
                <TableCell>
                  <Radio
                    size="small"
                    checked={projectManager?.memberId === applicant.memberId}
                    value={applicant.memberId}
                    onChange={onChangeProjectManager}
                  />
                </TableCell>
                <TableCell>
                  <Link
                    to={`/gesamtuebersicht/${applicant.memberId}`}
                    style={{ textDecoration: "none", color: "black" }}
                  >
                    {applicant.lastname}, {applicant.firstname}
                  </Link>
                </TableCell>
                <TableCell>{applicant.memberStatus.name}</TableCell>
                <TableCell>{dayjs(applicant.applicationDate).format("DD.MM.YYYY")}</TableCell>
                <TableCell>
                  <Link
                    to={`/projekte/${id}/projektbewerbungen/${applicant.memberId}`}
                    style={{ textDecoration: "none", color: "black" }}
                  >
                    <Button variant="outlined" color="primary" size="small">
                      Details {!isMobile && "anzeigen"}
                    </Button>
                  </Link>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => handleOpenPreviousApplications(applicant.memberId)}
                  >
                    Alte Bewerbungen
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {projectMembers.length === 0 ? (
        <Typography variant={"caption"} color={"error"}>
          Bitte wähle mindestens ein Mitglied aus.
        </Typography>
      ) : null}
      <Stack
        direction={isMobile ? "column" : "row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={{ marginTop: 10, marginBottom: 2 }}
      >
        <Stack direction={"column"}>
          <Button
            variant="contained"
            color="success"
            disabled={
              projectMembers.length === 0 ||
              !projectManager ||
              !kickOffDate ||
              kickOffDate < dayjs() ||
              staffingCommittee.length < 4 ||
              staffingCommittee.length > 6 ||
              projectMemberInStaffingCommittee
            }
            onClick={handleSaveTeamComposition}
          >
            <Groups />
            <Typography sx={{ marginLeft: 1 }}>Teamzusammenstellung bestätigen</Typography>
          </Button>
          {projectMemberInStaffingCommittee ? (
            <Typography variant={"caption"} color={"error"}>
              Ein Projektmitglied darf nicht im Besetzungsgremium sein.
            </Typography>
          ) : null}
        </Stack>
        <Typography variant="body2" sx={{ marginTop: isMobile ? 3 : 0, marginBottom: 2 }}>
          <strong>Hinweis:</strong> Alle beteiligten Mitglieder werden per E-Mail benachrichtigt.
        </Typography>
      </Stack>
    </Container>
  );
};

export default ProjectTeamComposition;
